"use client";

import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useRef } from "react";

interface User {
    name: string;
    designation: string;
    tasks: string;
}

interface ReviewProps {
    data: {
        id: number;
        title: string;
        description: string;
        reviewCard: User[];
        isLive: boolean;
    };
}

export default function Review_CleanMinimal({ data }: ReviewProps) {
    if (!data.isLive) return null;
    const swiperRef = useRef<any>(null);

    return (
        <section className="relative bg-gray-50 py-24  ">
            <div className="max-w-6xl mx-auto px-6">
                {/* Section header */}
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight">
                        {data.title}
                    </h2>
                    {data.description && (
                        <p className="mt-3 text-gray-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                            {data.description}
                        </p>
                    )}
                </motion.div>

                {/* Slider */}
                <div className="relative">
                    <Swiper
                        ref={swiperRef}
                        modules={[Autoplay, Navigation, Pagination]}
                        spaceBetween={30}
                        slidesPerView={1}
                        breakpoints={{
                            768: { slidesPerView: 2 },
                            1024: { slidesPerView: 3 },
                        }}
                        autoplay={{ delay: 5000 }}
                        pagination={{ clickable: true, el: ".custom-pagination" }}
                        className="pb-12"
                    >
                        {data.reviewCard?.map((review, index) => (
                            <SwiperSlide key={index} className="h-auto">
                                <motion.div
                                    className="bg-white border border-gray-100 p-6 md:p-8 shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col justify-between"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    {/* Stars */}
                                    <div className="flex mb-4">
                                        {[...Array(5)].map((_, i) => (
                                            <Star
                                                key={i}
                                                className={`w-5 h-5 ${i < 5 ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                                                    } mr-1`}
                                            />
                                        ))}
                                    </div>

                                    {/* Review text */}
                                    <p className="text-sm md:text-base text-gray-700 leading-relaxed flex-grow">
                                        “{review.tasks}”
                                    </p>

                                    {/* Author */}
                                    <div className="mt-6 pt-4 border-t border-gray-100">
                                        <h3 className="text-sm font-semibold text-gray-900">{review.name}</h3>
                                        <p className="text-xs text-gray-500">{review.designation}</p>
                                    </div>
                                </motion.div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    {/* Controls */}
                    <div className="absolute top-1/2 -translate-y-1/2 w-full flex justify-between px-2 sm:px-4">
                        <button
                            onClick={() => swiperRef.current?.swiper.slidePrev()}
                            className="p-3 bg-white border border-gray-200 hover:bg-gray-100 transition-colors shadow-sm"
                        >
                            <ChevronLeft className="w-5 h-5 text-gray-700" />
                        </button>
                        <button
                            onClick={() => swiperRef.current?.swiper.slideNext()}
                            className="p-3 bg-white border border-gray-200 hover:bg-gray-100 transition-colors shadow-sm"
                        >
                            <ChevronRight className="w-5 h-5 text-gray-700" />
                        </button>
                    </div>

                    {/* Pagination dots */}
                    <div className="custom-pagination flex justify-center mt-8" />
                </div>
            </div>
        </section>
    );
}
